import { pathOr } from 'ramda';

import { fetchCurrentUser, getUserToken } from '../../../../ducks/user.duck';
import { setInitialValues as setInitialValuesForPaymentMethods } from '../../../../ducks/paymentMethods.duck';
import { storableError } from '../../../../util/errors';
import { fetchStripePaymentMethodDetails as fetchStripePaymentMethodDetailsApi } from '../../../../util/api';
import * as log from '../../../../util/log';

export const getDefaultPaymentMethodFromCurrentUser = currentUser => {
  const defaultPaymentMethod = pathOr(null, ['stripeCustomer', 'defaultPaymentMethod'], currentUser);

  return pathOr(null, ['attributes', 'stripePaymentMethodId'], defaultPaymentMethod)
    ? defaultPaymentMethod
    : null;
};

export const getFallbackPaymentMethodDetails = paymentMethod => {
  const attributes = pathOr(null, ['attributes'], paymentMethod);

  return attributes
    ? {
        ...attributes,
        isUsable: true,
        requiresCartesBancairesResave: false,
      }
    : null;
};

// ================ Action types ================ //

export const SETUP_INTENT_REQUEST = 'app/PaymentMethodsTab/SETUP_INTENT_REQUEST';
export const SETUP_INTENT_SUCCESS = 'app/PaymentMethodsTab/SETUP_INTENT_SUCCESS';
export const SETUP_INTENT_ERROR = 'app/PaymentMethodsTab/SETUP_INTENT_ERROR';

export const STRIPE_CUSTOMER_REQUEST = 'app/PaymentMethodsTab/STRIPE_CUSTOMER_REQUEST';
export const STRIPE_CUSTOMER_SUCCESS = 'app/PaymentMethodsTab/STRIPE_CUSTOMER_SUCCESS';
export const STRIPE_CUSTOMER_ERROR = 'app/PaymentMethodsTab/STRIPE_CUSTOMER_ERROR';

export const STRIPE_PAYMENT_METHOD_DETAILS_REQUEST =
  'app/PaymentMethodsTab/STRIPE_PAYMENT_METHOD_DETAILS_REQUEST';
export const STRIPE_PAYMENT_METHOD_DETAILS_SUCCESS =
  'app/PaymentMethodsTab/STRIPE_PAYMENT_METHOD_DETAILS_SUCCESS';
export const STRIPE_PAYMENT_METHOD_DETAILS_ERROR =
  'app/PaymentMethodsTab/STRIPE_PAYMENT_METHOD_DETAILS_ERROR';

// ================ Reducer ================ //

const initialState = {
  setupIntentInProgress: false,
  setupIntentError: null,
  setupIntent: null,
  stripeCustomerFetched: false,
  stripePaymentMethodDetails: null,
  stripePaymentMethodDetailsFetchError: null,
};

export default function payoutMethodsTabReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SETUP_INTENT_REQUEST:
      return { ...state, setupIntentInProgress: true, setupIntentError: null };
    case SETUP_INTENT_SUCCESS:
      return {
        ...state,
        setupIntentInProgress: false,
        setupIntentError: null,
        setupIntent: payload,
      };
    case SETUP_INTENT_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return { ...state, setupIntentInProgress: false, setupIntentError: null };
    case STRIPE_CUSTOMER_REQUEST:
      return {
        ...state,
        stripeCustomerFetched: false,
        stripePaymentMethodDetailsFetchError: null,
      };
    case STRIPE_CUSTOMER_SUCCESS:
      return { ...state, stripeCustomerFetched: true };
    case STRIPE_CUSTOMER_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return { ...state, stripeCustomerFetchError: payload };
    case STRIPE_PAYMENT_METHOD_DETAILS_REQUEST:
      return {
        ...state,
        stripePaymentMethodDetailsFetchError: null,
      };
    case STRIPE_PAYMENT_METHOD_DETAILS_SUCCESS:
      return {
        ...state,
        stripePaymentMethodDetails: payload,
      };
    case STRIPE_PAYMENT_METHOD_DETAILS_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return {
        ...state,
        stripePaymentMethodDetails: null,
        stripePaymentMethodDetailsFetchError: payload,
      };
    default:
      return state;
  }
}

// ================ Action creators ================ //

export const setupIntentRequest = () => ({ type: SETUP_INTENT_REQUEST });
export const setupIntentSuccess = () => ({ type: SETUP_INTENT_SUCCESS });
export const setupIntentError = e => ({
  type: SETUP_INTENT_ERROR,
  error: true,
  payload: e,
});

export const stripeCustomerRequest = () => ({ type: STRIPE_CUSTOMER_REQUEST });
export const stripeCustomerSuccess = () => ({ type: STRIPE_CUSTOMER_SUCCESS });
export const stripeCustomerError = e => ({
  type: STRIPE_CUSTOMER_ERROR,
  error: true,
  payload: e,
});

export const stripePaymentMethodDetailsRequest = () => ({
  type: STRIPE_PAYMENT_METHOD_DETAILS_REQUEST,
});

export const stripePaymentMethodDetailsSuccess = payload => ({
  type: STRIPE_PAYMENT_METHOD_DETAILS_SUCCESS,
  payload,
});

export const stripePaymentMethodDetailsError = e => ({
  type: STRIPE_PAYMENT_METHOD_DETAILS_ERROR,
  error: true,
  payload: e,
});
// ================ Thunks ================ //

export const createStripeSetupIntent = () => (dispatch, getState, sdk) => {
  dispatch(setupIntentRequest());
  return sdk.stripeSetupIntents
    .create()
    .then(response => {
      const setupIntent = response.data.data;
      dispatch(setupIntentSuccess(setupIntent));
      return setupIntent;
    })
    .catch(e => {
      const error = storableError(e);
      log.error(error, 'create-setup-intent-failed');
      dispatch(setupIntentError(error));
      return { createStripeSetupIntentSuccess: false };
    });
};

export const stripeCustomer = () => (dispatch, getState, sdk) => {
  dispatch(stripeCustomerRequest());

  return dispatch(fetchCurrentUser({ include: ['stripeCustomer.defaultPaymentMethod'] }))
    .then(async currentUser => {
      const defaultPaymentMethod = getDefaultPaymentMethodFromCurrentUser(currentUser);
      const hasDefaultPaymentMethod = !!defaultPaymentMethod;

      if (hasDefaultPaymentMethod) {
        const stripePaymentMethodDetails = await dispatch(fetchStripePaymentMethodDetails(defaultPaymentMethod));

        if (!stripePaymentMethodDetails) {
          dispatch(stripePaymentMethodDetailsSuccess(getFallbackPaymentMethodDetails(defaultPaymentMethod)));
        }
      } else {
        dispatch(stripePaymentMethodDetailsSuccess(null));
      }

      dispatch(stripeCustomerSuccess());
    })
    .catch(e => {
      const error = storableError(e);
      log.error(error, 'fetch-stripe-customer-failed');
      dispatch(stripeCustomerError(error));
    });
};

export const fetchStripePaymentMethodDetails = defaultPaymentMethod => dispatch => {
  dispatch(stripePaymentMethodDetailsRequest());
  const token = getUserToken();
  const stripePaymentMethodId = pathOr(null, ['attributes', 'stripePaymentMethodId'], defaultPaymentMethod);
  const body = stripePaymentMethodId ? { stripePaymentMethodId } : {};

  return fetchStripePaymentMethodDetailsApi(body, token)
    .then(response => {
      const stripePaymentMethod =
        pathOr(null, ['data', 'stripePaymentMethod'], response) ||
        pathOr(null, ['stripePaymentMethod'], response);
      dispatch(stripePaymentMethodDetailsSuccess(stripePaymentMethod));
      return stripePaymentMethod;
    })
    .catch(e => {
      const error = storableError(e);
      log.error(error, 'fetch-stripe-payment-method-details-failed');
      dispatch(stripePaymentMethodDetailsError(error));
      return null;
    });
};

export const loadData = () => (dispatch, getState, sdk) => {
  dispatch(setInitialValuesForPaymentMethods());

  return dispatch(stripeCustomer());
};
